import React, { useRef, useState } from 'react';
import VideoCard from './VideoCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const VideoCarousel = ({ title, videos, loading }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.offsetWidth * 0.8;
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount 
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 0);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.offsetWidth - 10
    );
  };

  if (loading) {
    return (
      <div className="mb-12" data-testid={`carousel-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <h2 className="text-2xl font-bold mb-4 px-4 md:px-8">{title}</h2>
        <div className="flex gap-4 px-4 md:px-8 overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex-none w-64 h-36 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!videos || videos.length === 0) {
    return null;
  }

  return (
    <div 
      className="mb-12 group/carousel relative" 
      data-testid={`carousel-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <h2 className="text-2xl font-bold mb-4 px-4 md:px-8 text-foreground">
        {title}
      </h2>
      
      <div className="relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity ml-2"
            data-testid={`carousel-left-${title.toLowerCase().replace(/\s+/g, '-')}`}
            aria-label="Défiler vers la gauche"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Carousel Container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto scrollbar-hide px-4 md:px-8 scroll-smooth"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {videos.map((video) => (
            <div key={video.id} className="flex-none w-64">
              <VideoCard video={video} />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-background/80 hover:bg-background text-foreground p-2 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity mr-2"
            data-testid={`carousel-right-${title.toLowerCase().replace(/\s+/g, '-')}`}
            aria-label="Défiler vers la droite"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default VideoCarousel;
